import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicApi, settingsApi } from '@/lib/api';
import { defaultAnalysisData, calculateScores } from '@/lib/scoring';
import AnalysisWizard from '@/components/AnalysisWizard';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, TrendingUp, TrendingDown, Minus, ArrowLeft, Building, CloudOff, Cloud, FileDown, Calendar, Home, Clock, Euro, CheckCircle2, AlertTriangle, Link as LinkIcon, Copy, ExternalLink, Target, UserPlus, Share2, Mail, Forward } from 'lucide-react';
import SurveyForm from '@/components/SurveyForm';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function PublicWizard() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [draftId, setDraftId] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [thresholds, setThresholds] = useState(null);
  const [thresholdsList, setThresholdsList] = useState(null);
  const [repairItems, setRepairItems] = useState(null);
  const [costEstimates, setCostEstimates] = useState(null);
  const [modernizationItems, setModernizationItems] = useState(null);
  const [equipmentItems, setEquipmentItems] = useState(null);
  const [assessmentLabels, setAssessmentLabels] = useState(null);
  const [homepageText, setHomepageText] = useState(null);
  const lastSavedData = useRef(null);

  // Load existing analysis if ID is provided in URL
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl) {
      setLoading(true);
      publicApi.getAnalysis(idFromUrl, true)
        .then(res => {
          const data = res.data;
          // Check if analysis is already finalized
          if (data.status === 'finalized') {
            // Load as result view
            publicApi.getAnalysis(idFromUrl, false).then(r => setResult(r.data));
          } else {
            // Load as editable draft
            setInitialData(data);
            setDraftId(data.id);
            setAnalysisId(data.analysis_id);
          }
        })
        .catch(() => {
          toast.error('Analyse nicht gefunden');
        })
        .finally(() => {
          setLoading(false);
          setInitialLoading(false);
        });
    } else {
      setInitialLoading(false);
    }
  }, [searchParams]);

  // Load thresholds and repair items from server
  useEffect(() => {
    settingsApi.getThresholds()
      .then(res => setThresholds(res.data))
      .catch(() => console.log('Using default thresholds'));
    settingsApi.getThresholdsList()
      .then(res => setThresholdsList(res.data))
      .catch(() => console.log('Using default thresholds list'));
    settingsApi.getRepairItems()
      .then(res => setRepairItems(res.data))
      .catch(() => console.log('Using default repair items'));
    settingsApi.getCostEstimates()
      .then(res => setCostEstimates(res.data))
      .catch(() => console.log('Using default cost estimates'));
    settingsApi.getModernizationItems()
      .then(res => setModernizationItems(res.data))
      .catch(() => console.log('Using default modernization items'));
    settingsApi.getEquipmentItems()
      .then(res => setEquipmentItems(res.data))
      .catch(() => console.log('Using default equipment items'));
    settingsApi.getAssessmentLabels()
      .then(res => setAssessmentLabels(res.data))
      .catch(() => console.log('Using default assessment labels'));
    settingsApi.getHomepageText()
      .then(res => setHomepageText(res.data))
      .catch(() => console.log('Using default homepage text'));
  }, []);

  const handleAutoSave = useCallback(async (data) => {
    // Avoid saving if data hasn't changed
    const dataStr = JSON.stringify(data);
    if (dataStr === lastSavedData.current) return;
    
    setSaveStatus('saving');
    try {
      const res = await publicApi.saveDraft(data, draftId);
      lastSavedData.current = dataStr;
      if (!draftId && res.data.id) {
        setDraftId(res.data.id);
      }
      if (res.data.analysis_id && !analysisId) {
        setAnalysisId(res.data.analysis_id);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Auto-save error', err);
      setSaveStatus('error');
    }
  }, [draftId, analysisId]);

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      let res;
      if (draftId) {
        // Finalize existing draft
        res = await publicApi.finalizeDraft(draftId);
      } else {
        // Create new analysis
        res = await publicApi.submitAnalysis(data);
      }
      setResult(res.data);
      toast.success('Analyse erfolgreich eingereicht');
    } catch {
      toast.error('Fehler beim Einreichen');
    }
    setLoading(false);
  };

  if (result) {
    const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
    const handlePdfDownload = () => {
      toast.loading('PDF wird erstellt...', { duration: 5000 });
      window.open(`${API_URL}/api/public/properties/${result.id}/pdf`, '_blank');
      setTimeout(() => {
        toast('Analyse mit eigenem Firmennamen erstellen?', {
          description: 'Registrieren Sie sich kostenfrei und personalisieren Sie Ihre PDFs.',
          action: { label: 'Registrieren', onClick: () => window.location.href = '/registrieren' },
          duration: 8000,
        });
      }, 3000);
    };
    
    const handleCustomerPdfDownload = () => {
      toast.loading('Kunden-PDF wird erstellt...', { duration: 5000 });
      window.open(`${API_URL}/api/public/properties/${result.id}/pdf/customer`, '_blank');
      setTimeout(() => {
        toast('Analyse mit eigenem Firmennamen erstellen?', {
          description: 'Registrieren Sie sich kostenfrei und personalisieren Sie Ihre PDFs.',
          action: { label: 'Registrieren', onClick: () => window.location.href = '/registrieren' },
          duration: 8000,
        });
      }, 3000);
    };

    const analysisLink = `${window.location.origin}/analyse?id=${result.analysis_id || result.id}`;
    const shareSubject = `WEG-Analyse: ${result.name || result.analysis_id || 'Ergebnis'}`;
    const shareBody = `Hallo,\n\nich möchte Ihnen die Ergebnisse einer WEG-Analyse weiterleiten.\n\n` +
      `Objekt: ${result.name || '-'}\n` +
      `Einheiten: ${result.units}\n` +
      (result.demand_category ? `Verwaltungsbedarf: ${result.demand_category}\n` : '') +
      `\nAnalyse ansehen:\n${analysisLink}\n\n` +
      `---\nErstellt mit VerwalterHonorar.de – das WEG-Analysetool: ${window.location.origin}`;

    const handleShareEmail = () => {
      window.location.href = `mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareBody)}`;
    };

    const handleShareCopy = () => {
      const text = `WEG-Analyse: ${result.name || result.analysis_id}\n` +
        `${result.units} Einheiten` +
        (result.demand_category ? ` · Bedarf: ${result.demand_category}` : '') +
        `\n\n${analysisLink}`;
      navigator.clipboard.writeText(text);
      toast.success('Analyse-Info in Zwischenablage kopiert');
    };

    const handleShareTool = () => {
      const toolSubject = 'VerwalterHonorar.de – Verwaltungsbedarf analysieren. Angemessenes WEG-Verwalterhonorar ermitteln.';
      const toolBody = `Hallo,\n\nich möchte Ihnen VerwalterHonorar.de empfehlen – das WEG-Analysetool. Damit können Sie den Verwaltungsbedarf ` +
        `einer Wohnungseigentümergemeinschaft kostenlos und unverbindlich berechnen lassen.\n\n` +
        `Zum Tool:\n${window.location.origin}\n\n` +
        `Funktionen:\n` +
        `- Gebäudeanalyse & Zustandsbewertung\n` +
        `- Automatische Bedarfsermittlung\n` +
        `- Honorarempfehlung inkl. PDF-Report\n` +
        `- Kostenfrei, ohne Registrierung nutzbar\n\n` +
        `Viele Grüße`;
      window.location.href = `mailto:?subject=${encodeURIComponent(toolSubject)}&body=${encodeURIComponent(toolBody)}`;
    };

    const conditionLabels = {
      sehr_gut: 'Sehr gut',
      gut: 'Gut',
      mittel: 'Mittel',
      schlecht: 'Schlecht'
    };

    // German labels for renovation/modernization keys
    const renovationLabels = {
      heating: 'Heizungsanlage',
      windows: 'Fenster',
      facade: 'Fassade',
      facade_paint: 'Fassadenanstrich',
      insulation: 'Wärmedämmung',
      roof: 'Dach',
      electrical: 'Elektrik',
      plumbing: 'Sanitär',
      water_pipes: 'Wasserleitungen',
      elevator_modernization: 'Aufzugsmodernisierung',
      balconies: 'Balkone/Terrassen',
      basement: 'Keller',
      basement_sealing: 'Kellerabdichtung',
      common_areas: 'Gemeinschaftsflächen',
      entrance: 'Eingangsbereich',
      stairwell: 'Treppenhaus',
      garage: 'Garage/Stellplätze',
      garden: 'Gartenanlage',
      playground: 'Spielplatz',
      laundry: 'Waschküche',
      other: 'Sonstiges'
    };

    // German labels for repair keys
    const repairLabels = {
      heating_repair: 'Heizungsreparatur',
      elevator_repair: 'Aufzugsreparatur',
      roof_repair: 'Dachreparatur',
      facade_repair: 'Fassadenreparatur',
      plumbing_repair: 'Sanitärreparatur',
      electrical_repair: 'Elektroreparatur',
      water_damage: 'Wasserschaden',
      storm_damage: 'Sturmschaden',
      other: 'Sonstiges'
    };

    const getLabel = (key) => renovationLabels[key] || repairLabels[key] || key.replace(/_/g, ' ');

    const labelConfig = assessmentLabels?.[result.fee_assessment];
    const labelText = typeof labelConfig === 'object' ? labelConfig?.label : labelConfig;
    const clrName = (typeof labelConfig === 'object' ? labelConfig?.color : null) || ({ besonderer_bedarf: 'red', erhoeht: 'amber', reduziert: 'emerald', standard: 'blue' }[result.fee_assessment] || 'blue');
    const displayText = labelText || ({ besonderer_bedarf: 'Besonderer Bedarf', erhoeht: 'Erhöhter Aufwand', reduziert: 'Günstige Konditionen', standard: 'Standardvertrag' }[result.fee_assessment] || 'Standardvertrag');
    const colorMap = {
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', accent: 'bg-emerald-600' },
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', accent: 'bg-blue-600' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', accent: 'bg-amber-600' },
      red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', accent: 'bg-red-600' },
      violet: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', accent: 'bg-violet-600' },
      slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', accent: 'bg-slate-600' },
    };
    const cc = colorMap[clrName] || colorMap.blue;
    const recFee = result.recommended_fee || result.estimated_fee_per_unit;
    const u = result.units || 1;
    const mTotal = recFee ? Math.floor(recFee * u) : result.estimated_fee_monthly;
    const yTotal = mTotal ? mTotal * 12 : result.estimated_fee_yearly;
    const mGross = mTotal ? Math.floor(mTotal * 1.19) : null;
    const yGross = yTotal ? Math.floor(yTotal * 1.19) : null;

    // Use the ACTUAL demand_category from the backend, not a frontend guess
    const demandText = result.demand_category || 'Durchschnittlich';

    // Demand colors from backend's rating_color (configured via rating_labels)
    const ratingColorMap = {
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', stroke: 'stroke-emerald-500' },
      teal: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', stroke: 'stroke-teal-500' },
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', stroke: 'stroke-blue-500' },
      slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', stroke: 'stroke-slate-500' },
      yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', stroke: 'stroke-yellow-500' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', stroke: 'stroke-amber-500' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', stroke: 'stroke-orange-500' },
      red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', stroke: 'stroke-red-500' },
    };
    const demandColors = ratingColorMap[result.rating_color] || ratingColorMap.slate;

    const handleBackToEdit = () => {
      const aid = result.analysis_id || result.id;
      setResult(null);
      setLoading(true);
      publicApi.getAnalysis(aid, true)
        .then(res => { setInitialData(res.data); setDraftId(res.data.id); setAnalysisId(res.data.analysis_id); })
        .catch(() => toast.error('Analyse konnte nicht geladen werden'))
        .finally(() => setLoading(false));
    };

    return (
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 animate-fade-in" data-testid="public-result">
        {/* ===== COMPACT HEADER WITH ACTIONS ===== */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-sm">
              <CheckCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">{result.name || 'Analyse abgeschlossen'}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">{result.units} Einheiten · {result.analysis_id} · {result.city || ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="result-actions">
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-muted-foreground" onClick={handleBackToEdit} data-testid="back-to-edit-btn">
              <ArrowLeft className="h-3.5 w-3.5" /> Bearbeiten
            </Button>
            {result.pdf_download_enabled && (
              <>
                <Button size="sm" className="gap-1.5 text-xs bg-slate-900 hover:bg-slate-800 shadow-sm" onClick={handlePdfDownload} data-testid="download-pdf-btn">
                  <FileDown className="h-3.5 w-3.5" /> PDF herunterladen
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5 text-xs border-slate-300" onClick={handleCustomerPdfDownload} data-testid="download-customer-pdf-btn">
                  <FileDown className="h-3.5 w-3.5" /> Kunden-PDF
                </Button>
              </>
            )}
          </div>
        </div>

        {/* ===== HERO: TWO-COLUMN — Bewertung links, Honorar rechts ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-8">
          {/* LEFT: Bewertung (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            {/* Demand */}
            {result.demand_category && (
              <Card className="overflow-hidden border shadow-sm" data-testid="result-demand">
                <div className={`${demandColors.bg} border-b ${demandColors.border} px-5 py-3 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Target className={`h-4 w-4 ${demandColors.text}`} />
                    <span className={`text-sm font-semibold ${demandColors.text}`}>Verwaltungsbedarf</span>
                  </div>
                  <Badge className={`${demandColors.bg} ${demandColors.text} ${demandColors.border} border`}>{demandText}</Badge>
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center gap-6">
                    <div className="relative w-24 h-24 flex-shrink-0">
                      <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
                        <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" className="stroke-slate-100" />
                        <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" strokeDasharray={`${Math.min(result.demand_percentage, 100) * 0.975} 100`} strokeLinecap="round"
                          className={demandColors.stroke} />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-extrabold tabular-nums">{result.demand_percentage}%</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-bold mb-1">Bedarf: {demandText}</p>
                      <p className="text-xs text-muted-foreground">Gesamtscore: {result.total_score?.toFixed(1)} / 100</p>
                      {result.total_score != null && (
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-100">
                            <p className="text-[10px] font-medium text-blue-600 uppercase">Komplex.</p>
                            <p className="text-lg font-bold text-blue-900 tabular-nums">{result.building_complexity_score}</p>
                          </div>
                          <div className="text-center p-2 rounded-lg bg-purple-50 border border-purple-100">
                            <p className="text-[10px] font-medium text-purple-600 uppercase">Zustand</p>
                            <p className="text-lg font-bold text-purple-900 tabular-nums">{result.building_condition_score}</p>
                          </div>
                          <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-100">
                            <p className="text-[10px] font-medium text-amber-600 uppercase">Historie</p>
                            <p className="text-lg font-bold text-amber-900 tabular-nums">{result.history_score}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Building Details - compact */}
            {result.construction_year && (
              <Card>
                <CardContent className="p-5 space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2"><Home className="h-4 w-4 text-slate-400" /> Gebäudedetails</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <div className="p-2.5 bg-slate-50 rounded-lg"><span className="text-muted-foreground text-[11px] block">Baujahr</span><span className="font-semibold">{result.construction_year}</span></div>
                    {result.effective_year && result.effective_year !== result.construction_year && (
                      <div className="p-2.5 bg-blue-50 rounded-lg"><span className="text-blue-600 text-[11px] block">Eff. Baujahr</span><span className="font-semibold text-blue-900">{result.effective_year}</span></div>
                    )}
                    {result.size_category && <div className="p-2.5 bg-slate-50 rounded-lg"><span className="text-muted-foreground text-[11px] block">Größe</span><span className="font-semibold capitalize">{result.size_category}</span></div>}
                    {result.general_condition && <div className="p-2.5 bg-slate-50 rounded-lg"><span className="text-muted-foreground text-[11px] block">Zustand</span><span className="font-semibold">{conditionLabels[result.general_condition] || result.general_condition}</span></div>}
                  </div>
                  {result.renovations && Object.values(result.renovations).some(v => v) && (
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                      <span className="text-[11px] text-muted-foreground mr-1 self-center">Modernisierungen:</span>
                      {Object.entries(result.renovations).filter(([, v]) => v).map(([key]) => (
                        <Badge key={key} variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{getLabel(key)}</Badge>
                      ))}
                    </div>
                  )}
                  {result.planned_renovations && Object.values(result.planned_renovations).some(v => v) && (
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                      <span className="text-[11px] text-muted-foreground mr-1 self-center">Geplant:</span>
                      {Object.entries(result.planned_renovations).filter(([, v]) => v).map(([key]) => (
                        <Badge key={key} variant="secondary" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">{getLabel(key)}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Upcoming Refurbishment */}
            {result.upcoming_refurbishment && result.upcoming_refurbishment.length > 0 && (
              <Card>
                <CardContent className="p-5 space-y-2">
                  <p className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Sanierungsbedarf</p>
                  {result.upcoming_refurbishment.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2.5 bg-amber-50 rounded-lg border border-amber-100">
                      <span className="font-medium text-xs">{item.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-[11px]">{item.age} J.</span>
                        <Badge variant={item.urgency === 'hoch' ? 'destructive' : 'secondary'} className="text-[10px] h-5">{item.urgency === 'hoch' ? 'Dringend' : 'Mittel'}</Badge>
                      </div>
                    </div>
                  ))}
                  {result.estimated_refurbishment_costs > 0 && (
                    <div className="p-2.5 bg-amber-100 rounded-lg text-center text-sm">
                      <span className="text-amber-800">Kosten: </span><span className="font-bold text-amber-900">{result.estimated_refurbishment_costs.toLocaleString('de-DE')} €</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* RIGHT: Honorar (2 cols) */}
          <div className="lg:col-span-2">
            {(recFee || result.estimated_fee_monthly != null) && (
              <Card className="border shadow-lg overflow-hidden sticky top-4" data-testid="public-recommendation">
                {recFee ? (
                  <div>
                    <div className="bg-slate-900 p-6 text-white">
                      <p className="text-[11px] text-slate-400 uppercase tracking-widest mb-1 text-center">Empfohlenes Honorar</p>
                      <div className="flex items-baseline justify-center gap-1.5 mb-0.5">
                        <span className="text-5xl font-extrabold tabular-nums tracking-tight" data-testid="public-recommended-fee">{recFee.toFixed(0)}</span>
                        <span className="text-lg text-slate-400">€</span>
                      </div>
                      <p className="text-xs text-slate-500 text-center mb-4">pro Einheit / Monat netto</p>
                      <Separator className="bg-slate-700 mb-4" />
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="bg-slate-800 rounded-lg p-3 text-center">
                          <p className="text-[10px] text-slate-500 uppercase mb-1">Monatl. netto</p>
                          <p className="text-xl font-bold tabular-nums">{mTotal?.toLocaleString('de-DE')} €</p>
                        </div>
                        <div className="bg-slate-800 rounded-lg p-3 text-center">
                          <p className="text-[10px] text-slate-500 uppercase mb-1">Jährl. netto</p>
                          <p className="text-xl font-bold tabular-nums">{yTotal?.toLocaleString('de-DE')} €</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div><p className="text-[10px] text-slate-500">inkl. MwSt./Monat</p><p className="text-sm font-semibold tabular-nums text-slate-300">{mGross?.toLocaleString('de-DE')} €</p></div>
                        <div><p className="text-[10px] text-slate-500">inkl. MwSt./Jahr</p><p className="text-sm font-semibold tabular-nums text-slate-300">{yGross?.toLocaleString('de-DE')} €</p></div>
                      </div>
                      {result.estimated_renovation_fee > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-700 text-center">
                          <p className="text-[10px] text-slate-500 mb-0.5">Zzgl. Sondervergütung</p>
                          <p className="text-base font-semibold text-blue-400 tabular-nums">+{result.estimated_renovation_fee?.toLocaleString('de-DE')} €</p>
                        </div>
                      )}
                    </div>
                    <div className="p-4 space-y-3">
                      <p className="text-[11px] text-muted-foreground text-center">
                        {u} Einh.{result.base_fee_for_units ? ` · Grundhonorar ${Math.floor(result.base_fee_for_units)} €` : ''}
                      </p>
                      {result.minimum_fee_applied && (
                        <div className="text-[11px] px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-center" data-testid="public-minimum-fee-notice">
                          Mindesthonorar: <span className="font-semibold">{result.minimum_fee_effective || result.minimum_fee_value} €/Monat</span>
                        </div>
                      )}
                      {result.recommended_fee_min && result.recommended_fee_max && result.recommended_fee_min !== result.recommended_fee_max && (result.units > 6) && (
                        <div className="bg-slate-50 rounded-lg p-2.5 border">
                          <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Honorarspanne</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold tabular-nums text-slate-600">{Math.floor(result.recommended_fee_min)} €</span>
                            <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden relative">
                              <div className={`absolute h-full rounded-full ${cc.accent}`} style={{ left: `${Math.max(0, ((result.recommended_fee_min / result.recommended_fee_max) * 100) - 10)}%`, right: '0%' }} />
                            </div>
                            <span className="text-xs font-semibold tabular-nums text-slate-600">{Math.floor(result.recommended_fee_max)} €</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <CardContent className="p-5">
                    {result.estimated_fee_monthly != null && (
                      <div className="space-y-3">
                        <div className="p-4 rounded-xl bg-slate-50 border text-center">
                          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Monatlich netto</p>
                          <p className="text-3xl font-bold tabular-nums">{result.estimated_fee_monthly?.toLocaleString('de-DE')} €</p>
                        </div>
                        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                          <p className="text-xs font-medium text-emerald-600 uppercase mb-1">Jährlich netto</p>
                          <p className="text-3xl font-bold tabular-nums text-emerald-900">{result.estimated_fee_yearly?.toLocaleString('de-DE')} €</p>
                        </div>
                      </div>
                    )}
                    {result.recommendation_text && <p className="text-sm text-slate-600 leading-relaxed mt-3">{result.recommendation_text}</p>}
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        </div>

        {/* ===== "ANALYSE SPEICHERN" HINWEIS ===== */}
        <Card className="mb-8 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm" data-testid="save-analysis-cta">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm text-amber-900">Diese Analyse geht verloren!</p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  Ohne gespeicherten Link verlieren Sie den Zugang zu dieser Analyse. Mit einem <span className="font-semibold">kostenlosen Konto</span> werden alle Ihre Analysen automatisch gesichert — dauerhaft und jederzeit abrufbar.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <Link to="/registrieren">
                    <Button size="sm" className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-sm" data-testid="register-cta-btn">
                      <UserPlus className="h-3.5 w-3.5" /> Kostenfrei registrieren & sichern
                    </Button>
                  </Link>
                  <div className="flex items-center gap-1.5 text-[11px] text-amber-600">
                    <CheckCircle className="h-3 w-3" />
                    <span>Dauerhaft kostenfrei</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-amber-600">
                    <CheckCircle className="h-3 w-3" />
                    <span>Eigener Firmenname im PDF</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ===== WEITERLEITEN & TEILEN ===== */}
        <Card className="mb-8 border border-slate-200 bg-white" data-testid="share-section">
          <CardContent className="p-5 space-y-4">
            <p className="text-sm font-semibold flex items-center gap-2"><Share2 className="h-4 w-4 text-slate-400" /> Analyse weiterleiten</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button variant="outline" size="sm" className="gap-2 text-xs justify-start" onClick={handleShareEmail} data-testid="share-email-btn">
                <Mail className="h-3.5 w-3.5 text-blue-500" /> Per E-Mail senden
              </Button>
              <Button variant="outline" size="sm" className="gap-2 text-xs justify-start" onClick={handleShareCopy} data-testid="share-copy-btn">
                <Copy className="h-3.5 w-3.5 text-slate-500" /> Link & Info kopieren
              </Button>
              <Button variant="outline" size="sm" className="gap-2 text-xs justify-start" onClick={handleShareTool} data-testid="share-tool-btn">
                <Forward className="h-3.5 w-3.5 text-emerald-500" /> Tool empfehlen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ===== UMFRAGE ===== */}
        <div className="mb-8">
          <SurveyForm propertyId={result.id} analysisId={result.analysis_id} />
        </div>

        {/* ===== ANALYSE-LINK - ganz unten, dezent ===== */}
        <div className="border-t pt-6 mb-4" data-testid="analysis-link-info">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-2 flex-shrink-0">
              <LinkIcon className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Analyse-Link</span>
            </div>
            <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-slate-50/80 border border-slate-200/80 rounded-lg min-w-0">
              <code className="text-[11px] text-slate-500 flex-1 truncate font-mono" data-testid="analysis-link">
                {analysisLink}
              </code>
              <Button size="sm" variant="outline" className="h-6 px-2.5 text-[10px] gap-1 flex-shrink-0 border-slate-300"
                onClick={() => { navigator.clipboard.writeText(analysisLink); toast.success('Link kopiert'); }}
                data-testid="copy-link-btn">
                <Copy className="h-3 w-3" /> Kopieren
              </Button>
            </div>
            <Link to="/" className="flex-shrink-0">
              <Button variant="ghost" size="sm" className="text-xs gap-1.5 text-muted-foreground hover:text-slate-700"><Home className="h-3.5 w-3.5" /> Startseite</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Don't render the wizard until we've finished loading existing data (if any)
  const idFromUrl = searchParams.get('id');
  const isLoadingData = loading || (idFromUrl && initialLoading);

  return (
    <div className="max-w-5xl mx-auto py-12 px-6 animate-fade-in" data-testid="public-wizard-page">
      <div className="mb-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-primary flex items-center justify-center">
              <Building className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">WEG-Analyse</h1>
              <p className="text-base text-muted-foreground">Ermitteln Sie den Verwaltungsbedarf Ihrer Liegenschaft</p>
            </div>
          </div>
          {/* Auto-save status indicator */}
          <div className="flex items-center gap-2 text-xs" data-testid="save-status">
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1 text-muted-foreground animate-pulse">
                <Cloud className="h-4 w-4" /> Speichern...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Cloud className="h-4 w-4" /> Gespeichert
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1 text-red-600">
                <CloudOff className="h-4 w-4" /> Fehler
              </span>
            )}
          </div>
        </div>
      </div>
      {isLoadingData ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Analyse wird geladen...</p>
          </div>
        </div>
      ) : (
        <AnalysisWizard
          key={draftId || 'new'}
          initialData={initialData}
          onSubmit={handleSubmit}
          onDataChange={() => {}}
          onAutoSave={handleAutoSave}
          analysisId={analysisId}
          refurbishmentThresholds={thresholds}
          refurbishmentThresholdsList={thresholdsList}
          repairItems={repairItems}
          costEstimates={costEstimates}
          modernizationItems={modernizationItems}
          equipmentItems={equipmentItems}
          isAdmin={false}
          isEdit={!!initialData}
          loading={loading}
        />
      )}
    </div>
  );
}
